import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Button, Spinner } from "@heroui/react";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuUser,
    LuMapPin,
    LuBriefcase,
    LuImage,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { ThemedText, PageHeader, Stepper, ErrorState } from "@/components/ui";

// Import Steps
import {
    PersonalInfoStep,
    AddressStep,
    EmploymentStep,
    MediaDocumentStep,
} from "./components";

const STEPS = [
    { id: 1, title: "Data Diri", icon: LuUser },
    { id: 2, title: "KTP & Alamat", icon: LuMapPin },
    { id: 3, title: "Kepegawaian", icon: LuBriefcase },
    { id: 4, title: "Foto & Dokumen", icon: LuImage },
];

const StaffCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [currentStep, setCurrentStep] = useState(1);

    // React Hook Form
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        control,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            // Step 1: Staff Info
            name: "",
            handphone: "",
            whatsapp: "",
            create_user: false,
            email: "",
            password: "",
            password_confirmation: "",

            // Step 2: KTP
            identity_number: null,
            birth_place: "",
            birth_date: "",
            gender_id: "",
            religion_id: "",
            marital_status_id: "",
            identity_card_photo: [], // Array for ImageUploader
            is_domicile_same_as_ktp: true,
            ktp_address: {
                full_address: "",
                neighborhood_unit: "",
                sub_neighborhood_unit: "",
                village: "",
                district: "",
                city: "",
                province: "",
                postal_code: "",
                lat: null,
                lng: null,
                google_map_url: "",
            },

            // Step 3: Additional Docs & Domicile
            tax_identification_number: null,
            tax_identification_number_photo: [], // Array for ImageUploader
            domicile_address: {
                full_address: "",
                neighborhood_unit: "",
                sub_neighborhood_unit: "",
                village: "",
                district: "",
                city: "",
                province: "",
                postal_code: "",
                lat: null,
                lng: null,
                google_map_url: "",
            },

            // Step 4: Employment
            position_id: "",
            department_id: "",
            role_id: "",

            start_date: "",
            end_date: "",
            salary_type_id: "",
            salary: "",
            has_stopped_working: false,

            // Step 5: Media
            photo_profile: [],
            photo_personal: [],
        },
    });

    // Fetch Select Options
    const { data: religions } = useFetchData("select/religion");
    const { data: maritalStatuses } = useFetchData("select/marital-status");
    const { data: positions } = useFetchData("select/position");
    const { data: departments } = useFetchData("select/department");
    const { data: roles } = useFetchData("select/role");
    const { data: salaryTypes } = useFetchData("select/salary-type");

    // Fetch initial data for edit
    const fetchUrl = isEdit ? `staff/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    // Populate form on Edit
    useEffect(() => {
        if (isEdit && initialData) {
            // Prepare existing images for KTP photo
            const ktpImage = initialData.identity_card?.image;
            const existingKtpImage = ktpImage
                ? [
                      {
                          id: ktpImage.id,
                          preview: ktpImage.url_600,
                          isExisting: true,
                      },
                  ]
                : [];

            // Prepare existing images for NPWP photo
            const npwpImage = initialData.npwp_image;
            const existingNpwpImage = npwpImage
                ? [
                      {
                          id: npwpImage.id,
                          preview: npwpImage.url_600,
                          isExisting: true,
                      },
                  ]
                : [];

            // Prepare existing images for Profile photo
            const profileImage = initialData.profile_image;
            const existingProfileImage = profileImage
                ? [
                      {
                          id: profileImage.id,
                          preview: profileImage.url_600,
                          isExisting: true,
                      },
                  ]
                : [];

            // Prepare existing images for Personal photo
            const personalImage = initialData.personal_image;
            const existingPersonalImage = personalImage
                ? [
                      {
                          id: personalImage.id,
                          preview: personalImage.url_600,
                          isExisting: true,
                      },
                  ]
                : [];

            reset({
                name: initialData.name,
                handphone: initialData.handphone || "",
                whatsapp: initialData.whatsapp || "",

                // KTP
                identity_number:
                    initialData.identity_card?.identity_number || null,
                birth_place: initialData.identity_card?.birth_place || "",
                birth_date: initialData.identity_card?.birth_date || "",
                gender_id: String(initialData.identity_card?.gender_id || ""),
                religion_id: String(
                    initialData.identity_card?.religion_id || "",
                ),
                marital_status_id: String(
                    initialData.identity_card?.marital_status_id || "",
                ),
                is_domicile_same_as_ktp:
                    initialData.is_domicile_same_as_ktp || false,
                ktp_address: initialData.identity_card?.address || {},
                identity_card_photo: existingKtpImage,

                // Docs
                tax_identification_number:
                    initialData.tax_identification_number || "",
                tax_identification_number_photo: existingNpwpImage,
                domicile_address: initialData.address || {},

                // Employment
                position_id: String(initialData.position_id || ""),
                department_id: String(initialData.department_id || ""),
                role_id: String(initialData.user?.roles?.[0]?.id || ""),
                salary_type_id: String(initialData.salary_type_id || ""),
                salary: initialData.salary || "",
                start_date: initialData.start_date || "",
                end_date: initialData.end_date || "",
                has_stopped_working: !!initialData.end_date,

                // Media
                photo_profile: existingProfileImage,
                photo_personal: existingPersonalImage,
            });
        }
    }, [isEdit, initialData, reset]);

    const mutationEndpoint = isEdit ? `staff/${id}/update` : "staff/store";
    const staffMutation = usePostData(mutationEndpoint);

    const validateStep = async (step) => {
        let fields = [];
        switch (step) {
            case 1:
                fields = ["name", "whatsapp"];
                if (watch("create_user")) {
                    fields.push("email", "password", "password_confirmation");
                }
                break;
            case 2:
                fields = ["identity_number"];
                break;
            case 3:
                fields = [
                    "position_id",
                    "start_date",
                    "salary_type_id",
                    "salary",
                ];
                if (watch("has_stopped_working")) {
                    fields.push("end_date");
                }
                break;
            case 4:
                // Foto & Dokumen - all optional
                break;
            default:
                break;
        }
        return await trigger(fields);
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi informasi yang belum lengkap.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const onSubmit = (data) => {
        const formData = new FormData();

        // Handle Domicile Address logic - if same as KTP, copy KTP address
        const domicileAddress = data.is_domicile_same_as_ktp
            ? { ...data.ktp_address }
            : data.domicile_address;

        // Root level fields
        formData.append("name", data.name);
        if (data.whatsapp) formData.append("whatsapp", data.whatsapp);
        if (data.handphone) formData.append("handphone", data.handphone);

        // User account creation
        if (data.create_user) {
            formData.append("create_user", "1");
            formData.append("email", data.email);
            formData.append("password", data.password);
            formData.append("password_confirmation", data.password_confirmation);
        }

        // Identity Card (nested)
        formData.append(
            "identity_card[identity_number]",
            data.identity_number || "",
        );
        if (data.birth_place)
            formData.append("identity_card[birth_place]", data.birth_place);
        if (data.birth_date)
            formData.append("identity_card[birth_date]", data.birth_date);
        if (data.gender_id)
            formData.append("identity_card[gender_id]", data.gender_id);
        if (data.religion_id)
            formData.append("identity_card[religion_id]", data.religion_id);
        if (data.marital_status_id)
            formData.append(
                "identity_card[marital_status_id]",
                data.marital_status_id,
            );

        // KTP Address (nested inside identity_card)
        if (data.ktp_address) {
            Object.keys(data.ktp_address).forEach((key) => {
                if (
                    data.ktp_address[key] !== null &&
                    data.ktp_address[key] !== ""
                ) {
                    formData.append(
                        `identity_card[address][${key}]`,
                        data.ktp_address[key],
                    );
                }
            });
        }

        // Domicile Address (nested)
        if (domicileAddress) {
            Object.keys(domicileAddress).forEach((key) => {
                if (
                    domicileAddress[key] !== null &&
                    domicileAddress[key] !== ""
                ) {
                    formData.append(
                        `domicile_address[${key}]`,
                        domicileAddress[key],
                    );
                }
            });
        }

        // Employment (nested)
        if (data.position_id)
            formData.append("employment[position_id]", data.position_id);
        if (data.department_id)
            formData.append("employment[department_id]", data.department_id);
        if (data.role_id)
            formData.append("role_id", data.role_id);
        if (data.salary_type_id)
            formData.append("employment[salary_type_id]", data.salary_type_id);
        if (data.salary !== null && data.salary !== "")
            formData.append("employment[salary]", data.salary);
        if (data.start_date)
            formData.append("employment[start_date]", data.start_date);
        if (data.end_date)
            formData.append("employment[end_date]", data.end_date);
        if (data.tax_identification_number)
            formData.append(
                "employment[tax_identification_number]",
                data.tax_identification_number,
            );

        // Handle Images
        if (data.identity_card_photo && data.identity_card_photo.length > 0) {
            const file = data.identity_card_photo[0].file;
            if (file) formData.append("identity_card_photo", file);
        }

        if (
            data.tax_identification_number_photo &&
            data.tax_identification_number_photo.length > 0
        ) {
            const file = data.tax_identification_number_photo[0].file;
            if (file) formData.append("tax_identification_number_photo", file);
        }

        // Handle Profile Photo
        if (data.photo_profile && data.photo_profile.length > 0) {
            const file = data.photo_profile[0].file;
            if (file) formData.append("photo_profile", file);
        }

        // Handle Personal Photo
        if (data.photo_personal && data.photo_personal.length > 0) {
            const file = data.photo_personal[0].file;
            if (file) formData.append("photo_personal", file);
        }

        if (isEdit) {
            formData.append("_method", "PUT");
        }

        staffMutation.mutate(formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onSuccess: () => {
                showToast(
                    "success",
                    `Berhasil ${isEdit ? "mengupdate" : "menambahkan"} staff`,
                    `Staff berhasil ${isEdit ? "diperbarui" : "ditambahkan"}`,
                );
                navigate("/staff");
            },
            onError: (err) => {
                showToast(
                    "error",
                    `Gagal ${isEdit ? "mengupdate" : "menambahkan"} staff`,
                    err.response?.data?.message || "Terjadi kesalahan",
                );
            },
        });
    };

    // Error state for edit mode
    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data staff. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-4xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Staff" : "Tambah Staff"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Staff", href: "/staff" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            {/* Stepper */}
            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={STEPS} currentStep={currentStep} />
                </CardBody>
            </Card>

            {/* Form */}
            <form onSubmit={(e) => e.preventDefault()}>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {STEPS[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {/* Loading State */}
                        {isEdit && isFetching ? (
                            <div className="flex h-48 items-center justify-center">
                                <Spinner label="Memuat..." />
                            </div>
                        ) : (
                            <>
                                {currentStep === 1 && (
                                    <PersonalInfoStep
                                        register={register}
                                        errors={errors}
                                        control={control}
                                        watch={watch}
                                        religions={religions || []}
                                        maritalStatuses={maritalStatuses || []}
                                        roles={roles || []}
                                        isEdit={isEdit}
                                        hasUser={!!initialData?.user_id}
                                        userEmail={initialData?.user?.email || ""}
                                    />
                                )}

                                {currentStep === 2 && (
                                    <AddressStep
                                        register={register}
                                        errors={errors}
                                        control={control}
                                        watch={watch}
                                        setValue={setValue}
                                    />
                                )}

                                {currentStep === 3 && (
                                    <EmploymentStep
                                        errors={errors}
                                        control={control}
                                        watch={watch}
                                        positions={positions || []}
                                        departments={departments || []}
                                        salaryTypes={salaryTypes || []}
                                        isEdit={isEdit}
                                    />
                                )}

                                {currentStep === 4 && (
                                    <MediaDocumentStep control={control} />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Navigation Buttons */}
                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        size="sm"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/staff")
                                : handlePrev
                        }
                        startContent={<LuArrowLeft />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>

                    {currentStep < STEPS.length ? (
                        <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onPress={handleNext}
                            endContent={<LuArrowRight />}
                        >
                            Selanjutnya
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            size="sm"
                            isLoading={staffMutation.isLoading}
                            onPress={handleSubmit(onSubmit)}
                            startContent={
                                !staffMutation.isLoading && <LuSave />
                            }
                        >
                            {isEdit ? "Simpan Perubahan" : "Simpan Staff"}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default StaffCreate;
